import {
  createHashRouter,
  createPanel,
  createView,
  RoutesConfig,
} from '@vkontakte/vk-mini-apps-router';
import type { ModerationTarget } from '../api/types';
import { t } from '../i18n';

/**
 * Хеш-роутер — стандарт для мини-приложений ВК: обычные пути хостинг ВК
 * отдать не сможет, любой прямой переход по /me вернёт 404.
 *
 * View = вкладка таббара, Panel = экран внутри вкладки.
 */

export const VIEW = {
  REGISTRY: 'registry',
  FACES: 'faces',
  CHARACTER: 'character',
  TASKS: 'tasks',
  MORE: 'more',
} as const;

export type ViewId = (typeof VIEW)[keyof typeof VIEW];

export const PANEL = {
  /* Реестр */
  REGISTRY_LIST: 'registry.list',
  REGISTRY_CHARACTER: 'registry.character',
  /* Имущество чужого персонажа: плитки внизу досье ведут сюда. */
  REGISTRY_ITEMS: 'registry.items',
  REGISTRY_PETS: 'registry.pets',
  REGISTRY_ABILITIES: 'registry.abilities',

  /* Внешки */
  FACES_LIST: 'faces.list',
  FACES_ITEM: 'faces.item',

  /* Мой персонаж */
  CHARACTER_HUB: 'character.hub',
  CHARACTER_FORM: 'character.form',
  CHARACTER_FORM_DIFF: 'character.formDiff',
  CHARACTER_FORM_HISTORY: 'character.formHistory',
  CHARACTER_INVENTORY: 'character.inventory',
  CHARACTER_PETS: 'character.pets',
  CHARACTER_ABILITIES: 'character.abilities',
  CHARACTER_DECISIONS: 'character.decisions',

  /* Задачи */
  TASKS_QUEUE: 'tasks.queue',
  TASKS_FORM_REVIEW: 'tasks.formReview',
  TASKS_ITEM_REVIEW: 'tasks.itemReview',
  TASKS_PET_REVIEW: 'tasks.petReview',
  TASKS_ABILITY_REVIEW: 'tasks.abilityReview',

  /* Ещё */
  MORE_MENU: 'more.menu',
  MORE_PROFILE: 'more.profile',
  MORE_ADMIN: 'more.admin',
  MORE_RULES: 'more.rules',
  MORE_DATA_DELETION: 'more.dataDeletion',
} as const;

export type PanelId = (typeof PANEL)[keyof typeof PANEL];

export const routes = RoutesConfig.create([
  createView(VIEW.REGISTRY, [
    createPanel(PANEL.REGISTRY_LIST, '/', []),
    createPanel(PANEL.REGISTRY_CHARACTER, '/character/:characterId', []),
    createPanel(PANEL.REGISTRY_ITEMS, '/character/:characterId/items', []),
    createPanel(PANEL.REGISTRY_PETS, '/character/:characterId/pets', []),
    createPanel(PANEL.REGISTRY_ABILITIES, '/character/:characterId/abilities', []),
  ]),

  createView(VIEW.FACES, [
    createPanel(PANEL.FACES_LIST, '/faces', []),
    createPanel(PANEL.FACES_ITEM, '/faces/:faceClaimId', []),
  ]),

  createView(VIEW.CHARACTER, [
    createPanel(PANEL.CHARACTER_HUB, '/me', []),
    createPanel(PANEL.CHARACTER_FORM, '/me/form', []),
    createPanel(PANEL.CHARACTER_FORM_DIFF, '/me/form/diff', []),
    createPanel(PANEL.CHARACTER_FORM_HISTORY, '/me/form/history', []),
    createPanel(PANEL.CHARACTER_INVENTORY, '/me/inventory', []),
    createPanel(PANEL.CHARACTER_PETS, '/me/pets', []),
    createPanel(PANEL.CHARACTER_ABILITIES, '/me/abilities', []),
    createPanel(PANEL.CHARACTER_DECISIONS, '/me/decisions', []),
  ]),

  createView(VIEW.TASKS, [
    createPanel(PANEL.TASKS_QUEUE, '/tasks', []),
    createPanel(PANEL.TASKS_FORM_REVIEW, '/tasks/form/:versionId', []),
    createPanel(PANEL.TASKS_ITEM_REVIEW, '/tasks/item/:versionId', []),
    createPanel(PANEL.TASKS_PET_REVIEW, '/tasks/pet/:versionId', []),
    createPanel(PANEL.TASKS_ABILITY_REVIEW, '/tasks/ability/:versionId', []),
  ]),

  createView(VIEW.MORE, [
    createPanel(PANEL.MORE_MENU, '/more', []),
    createPanel(PANEL.MORE_PROFILE, '/more/profile', []),
    createPanel(PANEL.MORE_ADMIN, '/more/admin', []),
    createPanel(PANEL.MORE_RULES, '/more/rules', []),
    createPanel(PANEL.MORE_DATA_DELETION, '/more/data-deletion', []),
  ]),
]);

export const router = createHashRouter(routes.getRoutes());

/** Куда ведёт клик по вкладке таббара. */
export const VIEW_ROOT_PATH: Record<ViewId, string> = {
  [VIEW.REGISTRY]: '/',
  [VIEW.FACES]: '/faces',
  [VIEW.CHARACTER]: '/me',
  [VIEW.TASKS]: '/tasks',
  [VIEW.MORE]: '/more',
};

/** Панель, которая открывается первой внутри вкладки. */
export const VIEW_ROOT_PANEL: Record<ViewId, PanelId> = {
  [VIEW.REGISTRY]: PANEL.REGISTRY_LIST,
  [VIEW.FACES]: PANEL.FACES_LIST,
  [VIEW.CHARACTER]: PANEL.CHARACTER_HUB,
  [VIEW.TASKS]: PANEL.TASKS_QUEUE,
  [VIEW.MORE]: PANEL.MORE_MENU,
};

/**
 * Вкладки реестра и очередей живут в query, а не в отдельных панелях:
 * переключение вкладки не должно добавлять шаг в историю — иначе кнопка
 * «назад» в ВК начинает отматывать клики по табам вместо выхода с экрана.
 */
export const REGISTRY_TAB_PARAM = 'tab';

export const REGISTRY_TABS = ['personnel', 'alumni', 'resident', 'professor'] as const;
export type RegistryTab = (typeof REGISTRY_TABS)[number];

export const TASK_QUEUE_PARAM = 'queue';

export const TASK_QUEUE_LABELS: Record<ModerationTarget, string> = {
  form: t('review.queue.name.form'),
  item: t('review.queue.name.item'),
  pet: t('common.section.pets'),
  ability: t('common.section.abilities'),
};
